import { useState } from 'react';
import { riderWork as wiredRiderWork } from '../../api/riderWork';
import { refusalMessage } from '../../api/errors';
import { dataUrlToBlob, PACKAGE_PROBLEMS } from '../../lib/riderWork';
import PackagePhotoCapture from '../PackagePhotoCapture';
import './packageSeen.css';

/**
 * Something wrong with the parcel at the pickup (Rio, 11 Oct 2026): "what is
 * the package is not in good shape or matchs the description yes we will talk
 * to the client but who sees and the images we take". The rider says what is
 * wrong, adds a note and a photo, and sends it before collecting. The client
 * and the office see it. Then the rider calls the client, and either collects
 * the parcel anyway or gives the job back, which offers it to other riders.
 *
 * `reported` opens it on that decision: the rider reported earlier and came
 * back to the screen.
 */
export default function PackageProblemReport({ deliveryId, customerPhone, reported = false, onCollectAnyway, onHandedBack, onCancel, riderWork = wiredRiderWork }) {
  const [reasons, setReasons] = useState([]);
  const [note, setNote] = useState('');
  const [photo, setPhoto] = useState('');
  const [problem, setProblem] = useState('');
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(Boolean(reported));

  const toggle = (key) => setReasons((now) => (now.includes(key) ? now.filter((k) => k !== key) : [...now, key]));

  const send = async (e) => {
    e.preventDefault();
    if (reasons.length === 0) return setProblem('Choose what is wrong with the package.');
    if (reasons.includes('other') && !note.trim()) return setProblem('Please type a short note for "Other".');
    if (!photo) return setProblem('Take a photo that shows the problem.');
    setBusy(true);
    setProblem('');
    try {
      await riderWork.reportProblem(deliveryId, { reasons, note: note.trim() || null, photo: dataUrlToBlob(photo) });
      setSent(true);
    } catch (err) {
      setProblem(refusalMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const giveBack = async () => {
    setBusy(true);
    setProblem('');
    try {
      await riderWork.handBack(deliveryId, 'package_problem');
      onHandedBack();
    } catch (err) {
      setProblem(refusalMessage(err));
      setBusy(false);
    }
  };

  if (sent) {
    return (
      <section className="ps-card" aria-label="Problem reported">
        <h2 className="ps-h">Reported</h2>
        <p className="ps-hint">The client and the office can see what you reported and your photo. Talk to the client, then decide.</p>
        {problem ? (
          <p className="ps-error" role="alert">
            {problem}
          </p>
        ) : null}
        <div className="ps-actions">
          {customerPhone ? (
            <a className="ps-btn ps-btn--ghost" href={`tel:${String(customerPhone).replace(/\s/g, '')}`}>
              Call the client
            </a>
          ) : null}
          <button type="button" className="ps-btn" onClick={onCollectAnyway} disabled={busy}>
            Collect the package anyway
          </button>
          <button type="button" className="ps-btn ps-btn--danger" onClick={giveBack} disabled={busy}>
            {busy ? 'Giving it back…' : 'Give the job back'}
          </button>
        </div>
      </section>
    );
  }

  return (
    <form className="ps-card" aria-label="Report a problem with the package" onSubmit={send}>
      <h2 className="ps-h">What is wrong with the package?</h2>
      <div className="ps-reasons" role="group" aria-label="What is wrong">
        {PACKAGE_PROBLEMS.map(({ key, label }) => (
          <label key={key} className="ps-reason">
            <input type="checkbox" checked={reasons.includes(key)} onChange={() => toggle(key)} disabled={busy} />
            <span>{label}</span>
          </label>
        ))}
      </div>
      <label className="ps-label" htmlFor="ps-note">
        Note{reasons.includes('other') ? '' : ' (optional)'}
      </label>
      <textarea id="ps-note" className="ps-note" value={note} onChange={(e) => setNote(e.target.value)} maxLength={300} rows={3} disabled={busy} />
      <PackagePhotoCapture compact required value={photo} onChange={(url) => setPhoto(url || '')} label="A photo of the problem" hint="Show what is wrong: the client and the office see this photo." />
      {problem ? (
        <p className="ps-error" role="alert">
          {problem}
        </p>
      ) : null}
      <div className="ps-actions">
        <button type="submit" className="ps-btn" disabled={busy}>
          {busy ? 'Sending…' : 'Send the report'}
        </button>
        <button type="button" className="ps-btn ps-btn--ghost" onClick={onCancel} disabled={busy}>
          Back to the checklist
        </button>
      </div>
    </form>
  );
}
